import type { DomainEvent, DomainEventType } from '../core/domainEvents';
import type { AnalyticsContextSnapshot } from '../core/types';

import { PostHogEventName } from './eventNames';

export interface MappedPostHogEvent {
    eventName: string;
    properties: Record<string, unknown>;
}

function withContext(
    context: AnalyticsContextSnapshot,
    properties: Record<string, unknown>
): Record<string, unknown> {
    return {
        ...context,
        ...properties,
    };
}

export function mapDomainEventToPostHog(
    event: DomainEvent,
    context: AnalyticsContextSnapshot
): MappedPostHogEvent {
    switch (event.type) {
        case 'pageViewed':
            return {
                eventName: PostHogEventName.pageview,
                properties: withContext(context, {
                    $current_url: event.currentUrl,
                }),
            };

        case 'pageLeft':
            return {
                eventName: PostHogEventName.pageleave,
                properties: withContext(context, {
                    $current_url: event.currentUrl,
                }),
            };

        case 'sessionStarted':
            return {
                eventName: PostHogEventName.sessionStarted,
                properties: withContext(context, {
                    device_type: event.deviceType,
                    viewport_width: event.viewportWidth,
                    viewport_height: event.viewportHeight,
                }),
            };

        case 'sessionEnded':
            return {
                eventName: PostHogEventName.sessionEnded,
                properties: withContext(context, {
                    session_duration_ms: event.sessionDurationMs,
                }),
            };

        case 'loggedIn':
            return {
                eventName: PostHogEventName.login,
                properties: withContext(context, {}),
            };

        case 'appLoaded':
            return {
                eventName: PostHogEventName.appLoaded,
                properties: withContext(context, {
                    user_role: event.userRole,
                }),
            };

        case 'penSwitched':
            return {
                eventName: PostHogEventName.penSwitched,
                properties: withContext(context, {
                    source: event.source,
                }),
            };

        case 'penSwitchRendered':
            return {
                eventName: PostHogEventName.penSwitchRendered,
                properties: withContext(context, {
                    source: event.source,
                    duration_ms: event.durationMs,
                    overview_cache_warm: event.overviewCacheWarm,
                    cage_id: event.cageId,
                }),
            };

        case 'exportTriggered':
            return {
                eventName: PostHogEventName.exportTriggered,
                properties: withContext(context, {
                    export_type: event.exportType,
                    file_format: event.fileFormat,
                    ...(event.reportType ? { report_type: event.reportType } : {}),
                }),
            };

        case 'comparisonUsed':
            return {
                eventName: PostHogEventName.compareFunctionUsed,
                properties: withContext(context, {
                    source_page: event.sourcePage,
                    comparison_type: event.comparisonType,
                    time_frame_from: event.timeFrameFrom,
                    time_frame_to: event.timeFrameTo,
                    selected_pens: event.selectedPens,
                    selected_pen_count: event.selectedPenCount ?? event.selectedPens?.length,
                }),
            };

        case 'reportCreated':
            return {
                eventName: PostHogEventName.reportCreated,
                properties: withContext(context, {
                    report_type: event.reportType,
                }),
            };

        case 'reportDownloaded':
            return {
                eventName: PostHogEventName.reportDownloaded,
                properties: withContext(context, {
                    report_type: event.reportType,
                    file_format: event.fileFormat,
                }),
            };

        case 'featureActionUsed':
            return {
                eventName: PostHogEventName.featureUsed,
                properties: withContext(context, {
                    feature_name: event.featureName,
                }),
            };

        case 'errorEncountered':
            return {
                eventName: PostHogEventName.errorEncountered,
                properties: withContext(context, {
                    error_type: event.errorType,
                }),
            };

        case 'quickSearchOpened':
            return {
                eventName: PostHogEventName.quickSearchOpened,
                properties: withContext(context, {
                    source: event.source,
                }),
            };

        case 'quickSearchResultSelected':
            return {
                eventName: PostHogEventName.quickSearchResultSelected,
                properties: withContext(context, {
                    query_length: event.queryLength,
                    result_kind: event.resultKind,
                    result_group: event.resultGroup,
                    is_id_match: event.isIdMatch,
                    position: event.position,
                    result_count: event.resultCount,
                }),
            };

        case 'buttonClicked':
            return {
                eventName: PostHogEventName.buttonClicked,
                properties: withContext(context, {
                    tracking_id: event.trackingId,
                    label_source: event.labelSource,
                    ...(event.buttonVariant ? { button_variant: event.buttonVariant } : {}),
                }),
            };

        default: {
            const exhaustiveCheck: never = event;
            throw new Error(
                `Unhandled domain event type: ${(exhaustiveCheck as DomainEvent).type}`
            );
        }
    }
}

/** Maps each domain event type to its PostHog event name — used for exhaustiveness tests. */
export const domainEventToPostHogName: Record<DomainEventType, string> = {
    pageViewed: PostHogEventName.pageview,
    pageLeft: PostHogEventName.pageleave,
    sessionStarted: PostHogEventName.sessionStarted,
    sessionEnded: PostHogEventName.sessionEnded,
    loggedIn: PostHogEventName.login,
    appLoaded: PostHogEventName.appLoaded,
    penSwitched: PostHogEventName.penSwitched,
    penSwitchRendered: PostHogEventName.penSwitchRendered,
    exportTriggered: PostHogEventName.exportTriggered,
    comparisonUsed: PostHogEventName.compareFunctionUsed,
    reportCreated: PostHogEventName.reportCreated,
    reportDownloaded: PostHogEventName.reportDownloaded,
    featureActionUsed: PostHogEventName.featureUsed,
    errorEncountered: PostHogEventName.errorEncountered,
    quickSearchOpened: PostHogEventName.quickSearchOpened,
    quickSearchResultSelected: PostHogEventName.quickSearchResultSelected,
    buttonClicked: PostHogEventName.buttonClicked,
};
